import React, { useState, useContext, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import moment from "moment";
import { toast } from "sonner";
import { Box, Typography, Button, CircularProgress } from "@mui/material";

import { AuthContext } from "../../context/AuthContextProvider";
import loginhome from "../../assets/images/others/onboarding-home.jpg";
import DashedStepper from "../../components/DashedStepper";
import {
  userOnboarding,
  centerOnboarding,
  memberOnboarding,
  VerifyToken,
} from "../../api/auth";
import { AddCentre, useGetCentres, useGetUserProfile, syncSpandanReports, checkLegacyClinic } from "../../api/settings";
import { getPhoneNumberLimit } from "../../utils/phoneNumber";

import AccountStep from "./components/AccountStep";
import ProfileStep from "./components/ProfileStep";
import OrganisationStep from "./components/OrganisationStep";
import OrganisationSuccessStep from "./components/OrganisationSuccessStep";
import AddCenterStep from "./components/AddCenterStep";
import DoneStep from "./components/DoneStep";
import AddressDialog from "./components/AddressDialog";

const STORAGE_KEYS = {
  FORM1: "onboarding_form1",
  FORM2: "onboarding_form2",
  FORM3: "onboarding_form3",
  CENTER: "onboarding_center",
};

const DO_LATER_KEY = "spandan_do_later";
const REPORT_SYNC_DONE_KEY = "spandan_report_sync_done";

const STEPS = {
  ACCOUNT: 1,
  PROFILE: 2,
  ORG_FORM: 3,
  ORG_SUCCESS: 4,
  ADD_CENTER: 5,
  DONE: 6,
};

const parseBackendDOB = (dob) => {
  if (!dob) return null;
  const [day, month, year] = dob.split("-");
  return new Date(Number(year), Number(month) - 1, Number(day));
};

const Onboarding = () => {
  const { id } = useParams();
  const { user, login } = useContext(AuthContext);
  const { userProfile: data } = useGetUserProfile({});
  const { centres, isLoading: centresLoading, isValidating: centresValidating } = useGetCentres({
    page: 1,
    limit: 10,
  });

  const navigate = useNavigate();
  const [address, setaddress] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [step, setStep] = useState(Number(id) || 1);
  const [sameAsContact, setSameAsContact] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false); // step 1
  const [profileLoading, setProfileLoading] = useState(false); // step 2
  const [orgLoading, setOrgLoading] = useState(false); // step 3
  const [centerLoading, setCenterLoading] = useState(false); // step 5
  // userType stays plain "b2b" for migrated businesses too - migration is a
  // separate flag on the Business doc (see spandanMigrationController.js).
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;
  const [clinicPromptAnswered, setClinicPromptAnswered] = useState(false);
  const [autoCenterLoading, setAutoCenterLoading] = useState(false);
  const [showReportSyncPrompt, setShowReportSyncPrompt] = useState(false);
  const [reportSyncLoading, setReportSyncLoading] = useState(false);
  // null = not checked yet, true/false once the legacy-clinic check resolves.
  // The "migrate your clinic?" prompt only makes sense when there's actually an
  // old Spandan clinic on file for this phone number - otherwise it should go
  // straight to manual center creation instead of offering a migration that
  // would just 404 (see spandanMigrationController.js's checkLegacyClinic).
  const [hasLegacyClinic, setHasLegacyClinic] = useState(null);

  // Source of truth for "does a center already exist" is the server (via
  // useGetCentres), not local component state - so a page refresh re-derives
  // the right screen instead of resetting back to the first prompt.
  const hasCenter = Array.isArray(centres) && centres.length > 0;
  const isReportSyncDone = () => localStorage.getItem(REPORT_SYNC_DONE_KEY) === "true";

  // TEMP DEBUG - remove once the migrated-user step-5 flow is confirmed fixed.
  console.log("[onboarding-debug]", {
    step,
    isMigratedUser,
    hasCenter,
    centresLoading,
    centresValidating,
    centresCount: Array.isArray(centres) ? centres.length : "n/a",
    clinicPromptAnswered,
    showReportSyncPrompt,
    isReportSyncDone: isReportSyncDone(),
  });

  // Once a center exists, the "migrate your clinic?" question is moot - this
  // is checked directly in the render condition below via `hasCenter` rather
  // than latched into a separate boolean here. A one-way latch is dangerous:
  // useGetCentres's SWR cache key isn't scoped by business, so a stale
  // non-empty result left over from a previously-tested account in the same
  // browser session can flash `hasCenter=true` for a single render right
  // after a brand-new migrated business is created - latching that into
  // `clinicPromptAnswered` would permanently hide the prompt for the rest of
  // the session (only a hard refresh, which resets all local state, would
  // recover), even after SWR corrects itself on revalidation.

  // Show the report-sync prompt for migrated users once their center exists,
  // unless they've already answered it (persisted so a refresh doesn't re-ask).
  // Waits for the centres fetch to fully settle (`!centresValidating`) before
  // trusting `hasCenter` here - useGetCentres's SWR cache key isn't scoped by
  // business, so right after a brand-new migrated business is created, a
  // stale non-empty result left over from a previously-tested account in this
  // browser can flash `hasCenter=true` for a single render. Since this effect
  // (unlike the render check below) latches into state, trusting that flash
  // would permanently show the report-sync prompt and skip the clinic-migrate
  // prompt, even after SWR corrects itself moments later.
  useEffect(() => {
    if (
      isMigratedUser &&
      hasCenter &&
      !centresValidating &&
      !isReportSyncDone() &&
      step === STEPS.ADD_CENTER &&
      !showReportSyncPrompt
    ) {
      setShowReportSyncPrompt(true);
    }
  }, [isMigratedUser, hasCenter, centresValidating, step, showReportSyncPrompt]);

  // Resolve whether this migrated user actually has an old Spandan clinic on
  // file, once, right as they reach the add-center step with no center yet -
  // drives which of the two step-5 screens (migrate prompt vs manual add) renders.
  useEffect(() => {
    if (!isMigratedUser || hasCenter || hasLegacyClinic !== null) return;
    if (step !== STEPS.ADD_CENTER) return;

    let cancelled = false;
    checkLegacyClinic().then((response) => {
      if (!cancelled) setHasLegacyClinic(Boolean(response?.hasLegacyClinic));
    });
    return () => {
      cancelled = true;
    };
  }, [isMigratedUser, hasCenter, step, hasLegacyClinic]);

  useEffect(() => {
    if (centresLoading) return;
    if (!Array.isArray(centres)) return;

    // Migrated users get an extra "sync your reports?" prompt after the center
    // is created (auto or manual) - only skip straight to DONE once that's answered.
    if (
      centres.length > 0 &&
      step === STEPS.ADD_CENTER &&
      (!isMigratedUser || isReportSyncDone())
    ) {
      goToStep(STEPS.DONE);
    }
  }, [centres, centresLoading, step]);

  const tokenData = JSON.parse(localStorage.getItem("@auth_login"));
  const token = tokenData?.token;

  // go to a step AND update the URL so browser history works
  const goToStep = (nextStep) => {
    const normalized = Number(nextStep) || 1;
    setStep(normalized);
    navigate(`/onboarding/${normalized}`);
  };

  // when the URL changes (e.g. user presses browser back),
  // update local step state
  useEffect(() => {
    const current = Number(id) || 1;
    if (current !== step) {
      setStep(current);
    }
  }, [id, step]);

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.FORM1);
    return saved
      ? JSON.parse(saved)
      : {
        first_name: "",
        middle_name: "",
        last_name: "",
        phoneNumber: "",
        countryCode: "+91",
        email: "",
        password: "",
        confirmPassword: "",
      };
  });

  const [formData2, setFormData2] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.FORM2);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...parsed,
        dob: parsed.dob ? new Date(parsed.dob) : null,
      };
    }
    return {
      gender: "",
      dob: null,
      profession: "",
      qualification: [],
      medical_license_number: "",
    };
  });

  useEffect(() => {
    if (!data) return;

    // Fall back to the login/JWT payload (`user`, from AuthContext) for any
    // field getProfile doesn't have yet - e.g. a migrated business before it
    // has a masterUser Users record of its own.
    const firstNameFallback = user?.firstName || (user?.displayName || "").split(" ")[0];
    const lastNameFallback =
      user?.lastName || (user?.displayName || "").split(" ").slice(1).join(" ");

    // Step 1 fields
    setFormData((prev) => ({
      ...prev,
      first_name: data.firstName || firstNameFallback || "",
      middle_name: data.middleName || user?.middleName || "",
      last_name: data.lastName || lastNameFallback || "",
      phoneNumber: data.phoneNo || user?.phoneNo || "",
      countryCode: data.countryCode || user?.countryCode || "+91",
      email: data.email || user?.email || "",
      // keep any typed password values as-is
      password: prev.password,
      confirmPassword: prev.confirmPassword,
      virtualId: data.virtualId || user?.virtualId,
    }));
    // Step 2 fields
    setFormData2((prev) => ({
      ...prev,
      gender: data.gender || user?.gender || "",
      dob: parseBackendDOB(data?.dateOfBirth || user?.dateOfBirth),
      profession: data.profession || user?.profession || "",
      qualification: Array.isArray(data?.qualifications)
        ? data.qualifications.map((q) => String(q).trim())
        : Array.isArray(user?.qualifications)
          ? user.qualifications.map((q) => String(q).trim())
          : [],
      medical_license_number:
        data.medicalLicenseNumber || user?.medical_license_number || "",
    }));
    setFormData3((prev) => ({
      ...prev,
      organisationName: prev.organisationName || user?.businessName || "",
      organisationType: data.organisationType || "",
      organisationPhone: data.organisationPhone || user?.phoneNo || "",
    }));
    const isSame =
      (data.phoneNo || user?.phoneNo) &&
      data.organisationPhone &&
      (data.phoneNo || user?.phoneNo) === data.organisationPhone;

    setSameAsContact(isSame);

    // Organisation logo (step 3) and center logo (step 4) previews
    if (data?.organisationLogo) {
      setLogoPreview(data?.organisationLogo); // center logo (if you want same)
    }
  }, [data, user]);

  const step2Schema = Yup.object().shape({
    gender: Yup.string()
      .required("Gender is required")
      .oneOf(["Male", "Female", "Other"], "Please select a gender"),

    profession: Yup.string().required("Profession is required"),
    dob: Yup.date()
      .typeError("Please enter a valid date.")
      .required("Date of Birth is required.")
      .test("valid-year", "Please enter a valid year", function (value) {
        if (!value || !moment(value).isValid()) return false;

        const year = value.getFullYear();
        const currentYear = new Date().getFullYear();

        return year >= 1900 && year <= currentYear;
      })
      .test(
        "minimum-age",
        "You must be at least 18 years old",
        function (value) {
          if (!value || !moment(value).isValid()) return false;

          return value <= moment().subtract(18, "years").toDate();
        },
      ),
  });
  const [centerLogoPreview, setCenterLogoPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [orgLogoFile, setOrgLogoFile] = useState(null);
  const [centerLogoFile, setCenterLogoFile] = useState(null);

  const [orgImages, setOrgImages] = useState([]);

  const [formData3, setFormData3] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.FORM3);
    return saved
      ? JSON.parse(saved)
      : {
        organisationName: user?.businessName || "",
        organisationType: "",
        organisationPhone: "",
        organisationAddress: "",
      };
  });

  const step3Schema = Yup.object().shape({
    organisationName: Yup.string()
      .required("Organisation name is required")
      .min(2, "Organisation name must be at least 2 characters"),

    organisationType: Yup.string().required("Organisation type is required"),
  });

  const validateAndProceed3 = async () => {
    try {
      setOrgLoading(true);
      await step3Schema.validate(formData3, { abortEarly: false });
      setErrors({});
      await submitOrg(); // Proceed to next step
    } catch (err) {
      const formattedErrors = {};
      err.inner.forEach((e) => {
        if (e.path) formattedErrors[e.path] = e.message;
      });
      setErrors(formattedErrors);
    }
    setOrgLoading(false);
  };

  const submitOrg = async () => {
    const args = {
      organisationPhone: formData3.organisationPhone,
      organisationName: formData3.organisationName,
      organisationType: formData3.organisationType,
      centerAddress: formData3.organisationAddress,
    };
    const submitData = new FormData();
    submitData.append("stage", 3);
    submitData.append("centerDetails", JSON.stringify(args));
    if (orgLogoFile) {
      submitData.append("centerLogo", orgLogoFile);
    }
    orgImages.forEach((img) => {
      submitData.append("centerImage", img.file);
    });

    const response = await centerOnboarding(submitData);
    if (response.success) {
      const tokenResp = await VerifyToken({ token });
      login(tokenResp.data, tokenResp.token, false);
      if (user.userType === "b2b") {
        goToStep(STEPS.ORG_SUCCESS);
      } else {
        goToStep(STEPS.ADD_CENTER);
      }
    }
  };

  const validateAndProceed2 = async () => {
    try {
      setProfileLoading(true);
      await step2Schema.validate(formData2, { abortEarly: false });
      setErrors({});
      const args = {
        stage: 2,
        userDetails: {
          phoneNumber: formData.phoneNumber,
          firstName: formData.first_name,
          middleName: formData.middle_name,
          lastName: formData.last_name,
          gender: formData2.gender,
          dateOfBirth: moment(formData2.dob).format("DD-MM-YYYY"),
          profession: formData2.profession,
          email: formData.email,
          qualifications: formData2.qualification,
          medicalLicenseNumber: formData2.medical_license_number,
        },
      };
      var response;
      if (
        user.userType == "b2b-member" ||
        user.userType == "b2b-admin" ||
        user.userType == "b2b-viewer" ||
        user.userType == "b2b-doctor"
      ) {
        const tokenResp = await VerifyToken({ token });
        login(tokenResp.data, tokenResp.token, false);
        response = await memberOnboarding(args);
      } else {
        const tokenResp = await VerifyToken({ token });
        login(tokenResp.data, tokenResp.token, false);
        response = await userOnboarding(args);
      }
      setProfileLoading(false);
      if (response.success) {
        if (
          user.userType == "b2b-member" ||
          user.userType == "b2b-admin" ||
          user.userType == "b2b-viewer" ||
          user.userType == "b2b-doctor"
        ) {
          const tokenResp = await VerifyToken({ token });
          login(tokenResp.data, tokenResp.token, false);
          navigate("/dashboard", { replace: true });
        } else {
          goToStep(3);
        }
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      const formattedErrors = {};
      err.inner.forEach((e) => {
        if (e.path) formattedErrors[e.path] = e.message;
      });
      setErrors(formattedErrors);
    } finally {
      setProfileLoading(false);
    }
  };

  const passwordSchema = Yup.object({
    first_name: Yup.string()
      .trim()
      .required("First Name is required")
      .min(2, "Too Short!")
      .max(50, "Too Long!"),
    password: Yup.string()
      .required("Password is required")
      .min(10, "Minimum 10 characters")
      .matches(/[A-Z]/, "At least one uppercase letter")
      .matches(/[a-z]/, "At least one lowercase letter")
      .matches(/[0-9]/, "At least one number")
      .matches(/[@$!%*?&#]/, "At least one special character"),

    confirmPassword: Yup.string()
      .required("This field is required")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  });
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateAndProceed = async () => {
    try {
      setAccountLoading(true);
      await passwordSchema.validate(formData, { abortEarly: false });
      setErrors({});
      const args = {
        stage: 1,
        accountDetails: {
          firstName: formData.first_name,
          email: formData.email,
          lastName: formData.last_name,
          phone_number: formData.phoneNumber,
          password: formData.password,
        },
      };
      var response;
      if (
        user.userType == "b2b-member" ||
        user.userType == "b2b-admin" ||
        user.userType == "b2b-viewer" ||
        user.userType == "b2b-doctor"
      ) {
        response = await memberOnboarding(args);
      } else {
        response = await userOnboarding(args);
      }

      if (response.success) {
        goToStep(2);
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      const formattedErrors = {};

      err.inner.forEach((e) => {
        if (e.path) formattedErrors[e.path] = e.message;
      });
      setErrors(formattedErrors);
    } finally {
      setAccountLoading(false);
    }
  };

  const [fulladdress, setfulladdress] = useState({
    floor: "",
    area: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
    fulladdress: "",
    latitude: "",
    longitude: "",
  });

  const update_location = (location) => {
    const addressValue = {
      floor: location.floor,
      area: location.area,
      pincode: location.pincode,
      city: location.city,
      state: location.state,
      country: location.country,
      fulladdress: location.fulladdress,
      latitude: location.latitude,
      longitude: location.longitude,
    };

    setaddress(false);
    setfulladdress(addressValue);
    // Update address based on current step to avoid unintended prefill
    if (step === STEPS.ORG_FORM) {
      setFormData3((prev) => ({
        ...prev,
        organisationAddress: location.fulladdress,
      }));
    } else if (step === STEPS.ADD_CENTER) {
      setCenter((prev) => ({
        ...prev,
        centerAddress: `${location.floor}, ${location.area}, ${location.fulladdress}`,
      }));
    }
  };

  const handleChange = () => {
    setVisible(!visible);
  };

  const handleChange2 = () => {
    setVisible2(!visible2);
  };

  const [center, setCenter] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CENTER);
    const initialCenter = saved
      ? JSON.parse(saved)
      : {
        centerName: "",
        centerType: "",
        centerPhoneNumber: "",
        countryCode: "+91",
        centerAddress: "",
        businessName: "",
        businessTax: "",
        businessAddress: "",
      };

    return {
      ...initialCenter,
      countryCode: initialCenter.countryCode || "+91",
    };
  });
  const centerPhoneLimit = getPhoneNumberLimit(center.countryCode || "+91");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FORM1, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FORM2, JSON.stringify(formData2));
  }, [formData2]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.FORM3, JSON.stringify(formData3));
  }, [formData3]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CENTER, JSON.stringify(center));
  }, [center]);

  const [doLater, setDoLaterState] = useState(() => {
    const stored = localStorage.getItem(DO_LATER_KEY);
    return stored ? JSON.parse(stored) : false;
  });

  const setDoLater = (value) => {
    localStorage.setItem(DO_LATER_KEY, JSON.stringify(value));
    setDoLaterState(value); // triggers re-render
  };

  const addcenterSchema = Yup.object().shape({
    centerName: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Center Name is required"),
    centerType: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Center Type is required"),
    centerAddress: Yup.string()
      .min(2, "Too Short!")
      .max(500, "Too Long!")
      .required("Center Address is required"),
  });

  const handleAddCenter = async () => {
    try {
      setCenterLoading(true);
      await addcenterSchema.validate(center, { abortEarly: false });
      setErrors({});
      const args2 = {
        phone_number: user.phoneNumber,
        centerLogo: "",
        organisationPhone: center.centerPhoneNumber,
        organisationName: center.centerName,
        countryCode: center?.countryCode || "+91",
        type: center.centerType,
        address: fulladdress,
        centerImage: "",
        billingDetails: {
          name: center.businessName,
          code: center.businessStateCode,
          taxId: center.businessTax,
          address: {
            line: center.businessAddress,
            state: "",
            city: "",
            country: "",
            pincode: "",
          },
        },

        email: user.email,
        centerId: center.centerId,
      };
      const submitData = new FormData();
      submitData.append("centerDetails", JSON.stringify(args2));
      if (centerLogoFile) {
        submitData.append("centerLogo", centerLogoFile);
      }

      orgImages.forEach((img) => {
        submitData.append("centerImage", img.file);
      });

      submitData.append("userDetails", []);

      var response;

      response = await AddCentre(submitData);

      if (response.success) {
        toast.success(response.message);
        setDoLater(false);
        localStorage.setItem("centerAdd", true);
        if (isMigratedUser) {
          setShowReportSyncPrompt(true);
        } else {
          goToStep(STEPS.DONE);
        }
      } else if (!response.success) {
        toast.error(
          response.message?.includes("User already exists")
            ? "Center already exists in another business. Please try with another number."
            : response.message,
        );
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      const formattedErrors = {};

      // Only process Yup validation errors
      if (err.name === "ValidationError" && Array.isArray(err.inner)) {
        err.inner.forEach((e) => {
          if (e.path) formattedErrors[e.path] = e.message;
        });
        setErrors(formattedErrors);
      } else {
        // Handle other errors
        toast.error(err.message || "Something went wrong");
        console.error("Unexpected Error:", err);
      }
    } finally {
      setCenterLoading(false);
    }
  };

  // Migrated users with no legacy clinic to sync from get no manual form -
  // the center is created straight from the organisation details already
  // entered in step 3 (formData3), so they're never asked twice.
  const handleAutoCreateCenterFromOrg = async () => {
    try {
      setCenterLoading(true);
      const args2 = {
        phone_number: user.phoneNumber,
        centerLogo: "",
        organisationPhone: formData3.organisationPhone,
        organisationName: formData3.organisationName,
        countryCode: "+91",
        type: formData3.organisationType,
        address: {
          fulladdress: formData3.organisationAddress,
          city: "",
          state: "",
          country: "",
          pincode: "",
          longitude: 0,
          latitude: 0,
        },
        centerImage: "",
        billingDetails: {},
        email: user.email,
      };
      const submitData = new FormData();
      submitData.append("centerDetails", JSON.stringify(args2));
      submitData.append("userDetails", []);

      const response = await AddCentre(submitData);

      if (response.success) {
        toast.success(response.message || "Center set up from your organisation details");
        setDoLater(false);
        localStorage.setItem("centerAdd", true);
        setShowReportSyncPrompt(true);
      } else {
        toast.error(response.message || "Couldn't set up your center automatically.");
        // Last-resort fallback only - not a normal choice offered to the user.
        setClinicPromptAnswered(true);
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
      setClinicPromptAnswered(true);
    } finally {
      setCenterLoading(false);
    }
  };

  // "Migrate your clinic" prompt (migrated users only) - Yes calls
  // /centers with no centerDetails so the backend auto-creates from the old
  // Spandan clinic record; No falls through to the normal manual AddCenterStep.
  const handleAutoMigrateClinic = async () => {
    try {
      setAutoCenterLoading(true);
      const response = await AddCentre(new FormData());

      if (response.success) {
        toast.success(response.message || "Center created from your Spandan clinic");
        setDoLater(false);
        localStorage.setItem("centerAdd", true);
        setShowReportSyncPrompt(true);
      } else {
        toast.error(
          response.message ||
          "Couldn't find a Spandan clinic to migrate. Please add your center manually.",
        );
        setClinicPromptAnswered(true);
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong");
      setClinicPromptAnswered(true);
    } finally {
      setAutoCenterLoading(false);
    }
  };

  // Persists that the prompt was answered so a refresh doesn't ask again,
  // then either triggers the real sync or just moves on to DONE.
  const handleReportSyncChoice = async (shouldSync) => {
    localStorage.setItem(REPORT_SYNC_DONE_KEY, "true");

    if (shouldSync) {
      try {
        setReportSyncLoading(true);
        const response = await syncSpandanReports();
        if (response?.success) {
          toast.success(response.message || "Reports synced successfully");
        } else {
          toast.error(response?.message || "Couldn't sync your reports right now");
        }
      } catch (err) {
        toast.error(err.message || "Couldn't sync your reports right now");
      } finally {
        setReportSyncLoading(false);
      }
    }

    setShowReportSyncPrompt(false);
    goToStep(STEPS.DONE);
  };

  useEffect(() => {
    return () => {
      orgImages.forEach((img) => {
        if (img.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, [orgImages]);

  const completeOrganisation = async () => {
    try {
      setOrgLoading(true);

      const response = await userOnboarding({
        stage: 4,
      });

      if (response.success) {
        const tokenResp = await VerifyToken({ token });
        login(tokenResp.data, tokenResp.token, false);

        // Hard reload instead of goToStep() here - a fresh page load re-derives
        // hasCenter/hasLegacyClinic cleanly from the server for the just-created
        // business, avoiding the stale in-memory SWR/state races that only a
        // manual refresh was reliably clearing on this step before.
        window.location.href = `/onboarding/${STEPS.ADD_CENTER}`;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setOrgLoading(false);
    }
  };

  useEffect(() => {
    if (!user || centresLoading) return;

    const centerAdded = localStorage.getItem("centerAdd");
    const hasCenters = Array.isArray(centres) && centres.length > 0;

    if (Number(user.stage) === 0) {
      if (!hasCenters && step !== STEPS.ADD_CENTER && step !== STEPS.DONE) {
        goToStep(STEPS.ADD_CENTER);
        return;
      }

      // Migrated users get an extra "migrate your clinic?" / "sync your
      // reports?" pair of prompts before DONE - two things matter here:
      // 1. `centerAdded` is a global, non-business-scoped localStorage flag
      //    left over from ANY prior onboarding (even a different account) in
      //    this browser - trusting it for a migrated user risks jumping
      //    straight to DONE before they ever see the clinic-migration prompt.
      //    Only the live `hasCenters` signal (from useGetCentres) is trusted
      //    for them; normal b2b users keep the faster `centerAdded` shortcut.
      // 2. Even once a center genuinely exists, don't skip past the
      //    report-sync prompt until it's been answered.
      if (
        ((isMigratedUser ? hasCenters : (hasCenters || centerAdded === "true"))) &&
        !doLater &&
        step !== STEPS.DONE &&
        (!isMigratedUser || isReportSyncDone())
      ) {
        goToStep(STEPS.DONE);
        return;
      }

      if (hasCenters && doLater) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, centres, centresLoading, doLater, step]);

  const handleDoLater = async () => {
    try {
      const tokenResp = await VerifyToken({ token });

      // Update AuthContext + localStorage
      login(tokenResp.data, tokenResp.token, false);

      setDoLater(true);

      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error(err);

      setDoLater(true);
      navigate("/dashboard", { replace: true });
    }
  };

  return (
    <>
      <Helmet>
        <title>Spandan - Onboarding</title>
      </Helmet>

      <div
        className="main-wrapper"
        style={{ backgroundColor: "var(--app-surface)" }}
      >
        <div className="account-content">
          <div className="login-wrapper">
            <div className="login-img" style={{ paddingLeft: "0px" }}>
              <img src={loginhome} alt="img" />
            </div>

            <div className="login-content" style={{ marginTop: "0px" }}>
              <div className="login-userset">
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    mb: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: {
                        xs: "90%",
                        sm: "85%",
                        md: "80%",
                      },
                      maxWidth: "650px",
                    }}
                  >
                    <DashedStepper activeStep={step} />
                  </Box>
                </Box>
                <div className="mt-20">
                  {step == 1 ? (
                    <AccountStep
                      formData={formData}
                      errors={errors}
                      handleInputChange={handleInputChange}
                      setFormData={setFormData}
                      visible={visible}
                      onToggleVisible={handleChange}
                      visible2={visible2}
                      onToggleVisible2={handleChange2}
                      onProceed={validateAndProceed}
                      loading={accountLoading}
                    />
                  ) : step == 2 ? (
                    <ProfileStep
                      formData2={formData2}
                      setFormData2={setFormData2}
                      errors={errors}
                      onBack={() => goToStep(step - 1)}
                      onProceed={validateAndProceed2}
                      loading={profileLoading}
                    />
                  ) : step == 3 ? (
                    <OrganisationStep
                      orgLogoFile={orgLogoFile}
                      setOrgLogoFile={setOrgLogoFile}
                      logoPreview={logoPreview}
                      setLogoPreview={setLogoPreview}
                      formData3={formData3}
                      setFormData3={setFormData3}
                      errors={errors}
                      data={data}
                      sameAsContact={sameAsContact}
                      setSameAsContact={setSameAsContact}
                      contactPhoneNumber={formData.phoneNumber}
                      onBack={() => goToStep(step - 1)}
                      onProceed={validateAndProceed3}
                      loading={orgLoading}
                    />
                  ) : step === STEPS.ORG_SUCCESS ? (
                    <OrganisationSuccessStep
                      onBack={() => goToStep(STEPS.ORG_FORM)}
                      onProceed={completeOrganisation}
                      loading={orgLoading}
                    />
                  ) : step === STEPS.ADD_CENTER && isMigratedUser && showReportSyncPrompt ? (
                    <Box sx={{ textAlign: "center", padding: "24px 8px" }}>
                      <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#22223b", marginBottom: "10px" }}>
                        Do you want to sync your Spandan reports to Spandan One?
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#66647a", marginBottom: "22px" }}>
                        You can bring over ECG reports from your old Spandan account.
                      </Typography>
                      <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                        <Button
                          variant="outlined"
                          disabled={reportSyncLoading}
                          onClick={() => handleReportSyncChoice(false)}
                          sx={{ textTransform: "none", borderRadius: "9px" }}
                        >
                          Not now
                        </Button>
                        <Button
                          variant="contained"
                          disabled={reportSyncLoading}
                          onClick={() => handleReportSyncChoice(true)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "9px",
                            backgroundColor: "#6c45ff",
                            boxShadow: "none",
                            "&:hover": { backgroundColor: "#5d38eb", boxShadow: "none" },
                          }}
                        >
                          {reportSyncLoading ? "Syncing..." : "Yes, sync my reports"}
                        </Button>
                      </Box>
                    </Box>
                  ) : step === STEPS.ADD_CENTER && isMigratedUser && !hasCenter && !clinicPromptAnswered && hasLegacyClinic === null ? (
                    <Box sx={{ textAlign: "center", padding: "60px 8px" }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : step === STEPS.ADD_CENTER && isMigratedUser && !hasCenter && !clinicPromptAnswered && hasLegacyClinic === true ? (
                    <Box sx={{ textAlign: "center", padding: "24px 8px" }}>
                      <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#22223b", marginBottom: "10px" }}>
                        Set up your center
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#66647a", marginBottom: "22px" }}>
                        We found a clinic on your old Spandan account - we can set up
                        your center using those details.
                      </Typography>
                      <Button
                        variant="contained"
                        disabled={autoCenterLoading}
                        onClick={handleAutoMigrateClinic}
                        sx={{
                          textTransform: "none",
                          borderRadius: "9px",
                          backgroundColor: "#6c45ff",
                          boxShadow: "none",
                          "&:hover": { backgroundColor: "#5d38eb", boxShadow: "none" },
                        }}
                      >
                        {autoCenterLoading ? "Setting up..." : "Create Center"}
                      </Button>
                    </Box>
                  ) : step === STEPS.ADD_CENTER && isMigratedUser && !hasCenter && !clinicPromptAnswered && hasLegacyClinic === false ? (
                    <Box sx={{ textAlign: "center", padding: "24px 8px" }}>
                      <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#22223b", marginBottom: "10px" }}>
                        Set up your center
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#66647a", marginBottom: "22px" }}>
                        We can set up your center using the same details as your organisation
                        ({formData3.organisationName || "your organisation"}).
                      </Typography>
                      <Button
                        variant="contained"
                        disabled={centerLoading}
                        onClick={handleAutoCreateCenterFromOrg}
                        sx={{
                          textTransform: "none",
                          borderRadius: "9px",
                          backgroundColor: "#6c45ff",
                          boxShadow: "none",
                          "&:hover": { backgroundColor: "#5d38eb", boxShadow: "none" },
                        }}
                      >
                        {centerLoading ? "Setting up..." : "Create Center"}
                      </Button>
                    </Box>
                  ) : step === STEPS.ADD_CENTER ? (
                    <AddCenterStep
                      centerLogoFile={centerLogoFile}
                      setCenterLogoFile={setCenterLogoFile}
                      centerLogoPreview={centerLogoPreview}
                      setCenterLogoPreview={setCenterLogoPreview}
                      center={center}
                      setCenter={setCenter}
                      errors={errors}
                      centerPhoneLimit={centerPhoneLimit}
                      onOpenAddressSearch={() => setaddress(true)}
                      orgImages={orgImages}
                      setOrgImages={setOrgImages}
                      onBack={() => goToStep(step - 1)}
                      onProceed={handleAddCenter}
                      loading={centerLoading}
                    />
                  ) : (
                    <DoneStep
                      showAllSetBanner={
                        step === STEPS.DONE && doLater === false
                      }
                      hideAddMoreCentres={isMigratedUser}
                      onAddMoreCentres={() => {
                        setDoLater(true);
                        navigate("/add-centre", { replace: true });
                      }}
                      onAddTeam={() => {
                        setDoLater(true);
                        navigate("/add-member", { replace: true });
                      }}
                      onDoLater={handleDoLater}
                    />
                  )}
                </div>

                <AddressDialog
                  open={address}
                  onClose={() => setaddress(false)}
                  onAddressChange={update_location}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Onboarding;
